import { call } from '@advanza/api'
import { InputField } from '@advanza/input'
import { Button, Icon } from '@advanza/ui'
import Card from 'components/Card'
import Page from 'components/ui/Page'
import { useCurrentCountry } from 'hooks/miscHooks'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    buildQueryString,
    ChatbotReportSource,
    DEFAULT_SOURCE,
    defaultEndDate,
    defaultStartDate,
    ENDPOINT,
    EXPORT_ENDPOINT,
    formatPercent,
    SOURCE_OPTIONS,
    sourceLabel,
} from './chatbotAnalyticsReportUtils'

interface ChatsStarted {
    events: number
    distinct_sessions: number
}

interface RagBucket {
    success: number
    fallback: number
    success_rate_pct: number
}

interface ConversionBucket {
    total: number
    distinct_sessions: number
    by_type: Record<string, number>
}

interface TriggerBucket {
    events: number
    distinct_sessions: number
}

interface FlowBucket {
    events: number
    distinct_sessions: number
}

interface WizardOpenedBucket {
    events: number
    distinct_sessions: number
    by_trigger: Record<string, TriggerBucket>
    by_flow: Record<string, FlowBucket>
}

interface WizardFinishedBucket {
    from_bot: number
    from_user_click: number
    total_attributed: number
}

interface CtaUsageBucket {
    total_sessions: number
    with_cta: number
    without_cta: number
}

interface DerivedBucket {
    conversion_rate_pct: number
    rag_intent_completion_pct: number
    user_click_completion_pct: number
}

interface FunnelBucket {
    chat_sessions: number
    in_chat_wizard_sessions: number
    in_chat_lead_sessions: number
    in_chat_wizard_open_rate_pct: number
    in_chat_lead_conversion_rate_pct: number
    in_chat_wizard_to_lead_rate_pct: number
}

interface QuoteRedirectBucket {
    quote_wizard_sessions: number
    quote_requests: number
    quote_conversion_rate_pct: number
}

interface InstallsBucket {
    new_installs: number
    active_installs: number
}

interface ChatbotImpactBucket {
    in_chat_leads: number
    quote_requests: number
    total_leads: number
    chatbot_conversion_rate_pct: number
}

interface FiltersApplied {
    start_date: string
    end_date: string
    country: string | null
    provider_id: number | null
    source: ChatbotReportSource
}

interface ChatbotAnalyticsReport {
    filters_applied: FiltersApplied
    installs: InstallsBucket
    chatbot_impact: ChatbotImpactBucket
    funnel: FunnelBucket
    quote_redirect: QuoteRedirectBucket
    chats_started: ChatsStarted
    rag: RagBucket
    conversions: ConversionBucket
    wizard_opened: WizardOpenedBucket
    wizard_finished: WizardFinishedBucket
    cta_usage: CtaUsageBucket
    derived: DerivedBucket
}

const quoteFlowStaysInChat = (source: ChatbotReportSource): boolean => source !== 'profile_chatbot'

const ChatbotAnalyticsReportPage = () => {
    const country = useCurrentCountry()
    const [startDate, setStartDate] = useState<string>(defaultStartDate())
    const [endDate, setEndDate] = useState<string>(defaultEndDate())
    const [providerId, setProviderId] = useState<string>('')
    const [source, setSource] = useState<ChatbotReportSource>(DEFAULT_SOURCE)
    const [scopeCountry, setScopeCountry] = useState<boolean>(true)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string>('')
    const [report, setReport] = useState<ChatbotAnalyticsReport | null>(null)
    const [isExporting, setIsExporting] = useState<boolean>(false)

    const effectiveCountry = useMemo<string | null>(() => {
        if (!scopeCountry) {
            return null
        }
        return country ? country.toString().toUpperCase() : null
    }, [country, scopeCountry])

    const queryString = useMemo(
        () => buildQueryString(startDate, endDate, effectiveCountry, providerId, source),
        [startDate, endDate, effectiveCountry, providerId, source],
    )

    const loadReport = useCallback(async () => {
        setLoading(true)
        setError('')
        setReport(null)
        try {
            const response = await call(withQueryString(ENDPOINT, queryString))
            if (response?.status === 'success' && response?.chats_started) {
                setReport(response as unknown as ChatbotAnalyticsReport)
            } else {
                setError(response?.error || 'Failed to load report')
            }
        } catch (err) {
            setError('Error: ' + (err as Error).message)
        } finally {
            setLoading(false)
        }
    }, [queryString])

    const handleExport = useCallback(() => {
        if (isExporting) {
            return
        }
        setIsExporting(true)
        const base = `${window.location.origin}/api/${EXPORT_ENDPOINT}`
        window.open(withQueryString(base, queryString), '_blank', 'noopener,noreferrer')
        window.setTimeout(() => setIsExporting(false), 1500)
    }, [queryString, isExporting])

    useEffect(() => {
        loadReport()
    }, [])

    return (
        <Page title="Chatbot Analytics">
            <div className="space-y-5">
                <div>
                    <h1 className="text-2xl font-semibold">Chatbot Analytics</h1>
                    <p className="text-grey-700 mt-1">
                        Aggregated KPIs for the chatbot widget: chats, RAG performance, wizard
                        funnel, CTA engagement and lead conversions.
                    </p>
                </div>

                <div className="flex flex-wrap items-end gap-3 rounded-lg border border-grey-200 bg-white p-4">
                    <div>
                        <label className="block text-sm font-medium text-grey-700 mb-1">
                            Start date
                        </label>
                        <InputField
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-grey-700 mb-1">
                            End date
                        </label>
                        <InputField
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-grey-700 mb-1">
                            Provider ID
                        </label>
                        <InputField
                            type="number"
                            placeholder="All providers"
                            value={providerId}
                            onChange={(e) => setProviderId(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-grey-700 mb-1">
                            Source
                        </label>
                        <select
                            className="h-10 rounded border border-grey-200 bg-white px-2 text-md text-grey-700"
                            value={source}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                setSource(e.target.value as ChatbotReportSource)
                            }>
                            {SOURCE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-grey-700 mb-1">
                            Country scope
                        </label>
                        <label className="flex items-center gap-2 text-md text-grey-700">
                            <input
                                type="checkbox"
                                checked={scopeCountry}
                                onChange={(e) => setScopeCountry(e.target.checked)}
                            />
                            <span>
                                Limit to <strong>{country?.toString().toUpperCase() || '?'}</strong>
                            </span>
                        </label>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={loadReport}
                            disabled={loading}
                            before={<Icon name="refresh" />}>
                            {loading ? 'Loading…' : 'Apply'}
                        </Button>
                        <Button
                            onClick={handleExport}
                            disabled={isExporting || loading || !report}
                            before={<Icon name="file_download" />}>
                            Export CSV
                        </Button>
                    </div>
                </div>

                {error !== '' && (
                    <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-md">
                        {error}
                    </div>
                )}

                {loading && !report && (
                    <div className="flex flex-col items-center justify-center py-12">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                        <p className="mt-3 text-md text-grey-500">Loading chatbot analytics…</p>
                    </div>
                )}

                {report && (
                    <div className="space-y-5">
                        <p className="text-md text-grey-500">
                            Date range:{' '}
                            <span className="font-medium text-grey-700">
                                {report.filters_applied.start_date}
                            </span>
                            {' → '}
                            <span className="font-medium text-grey-700">
                                {report.filters_applied.end_date}
                            </span>
                            {report.filters_applied.country !== null && (
                                <>
                                    {' · '}Country:{' '}
                                    <span className="font-medium text-grey-700">
                                        {report.filters_applied.country}
                                    </span>
                                </>
                            )}
                            {report.filters_applied.provider_id !== null && (
                                <>
                                    {' · '}Provider:{' '}
                                    <span className="font-medium text-grey-700">
                                        {report.filters_applied.provider_id}
                                    </span>
                                </>
                            )}
                            {' · '}Source:{' '}
                            <span className="font-medium text-grey-700">
                                {sourceLabel(report.filters_applied.source)}
                            </span>
                        </p>

                        {report.filters_applied.source === 'conversion_bot' && (
                            <Card header="Website installs">
                                <p className="text-sm text-grey-500 mb-3">
                                    Taken from the heartbeat the widget stamps on every page load of
                                    a provider&apos;s own website, so a live snippet counts even
                                    when no visitor ever opened the chat.{' '}
                                    <strong>Active chatbots</strong> reads <code>last_seen_at</code>
                                    , which keeps only the latest heartbeat: it is exact for a range
                                    ending today, but undercounts an older range, because a provider
                                    still live today has since moved out of it.{' '}
                                    <strong>New installs</strong> reads <code>first_seen_at</code>,
                                    which is never rewritten, and is exact for any range.
                                </p>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <KpiTile
                                        label="New installs"
                                        value={report.installs.new_installs}
                                        color="#0284c7"
                                        sub="first seen in period"
                                    />
                                    <KpiTile
                                        label="Active chatbots"
                                        value={report.installs.active_installs}
                                        color="#0284c7"
                                        sub="last seen in period"
                                    />
                                </div>
                            </Card>
                        )}

                        <Card header="Chatbot impact (in-chat + quote redirect)">
                            <p className="text-sm text-grey-500 mb-3">
                                {quoteFlowStaysInChat(report.filters_applied.source) ? (
                                    <>
                                        Request rows matched back via{' '}
                                        <code>chatbot_session_id</code>. In-chat volume is callback,
                                        quote request, appointment, and message submissions written
                                        in the chat. Quote volume is only{' '}
                                        <code>request_type = quote</code> hand-offs to the request
                                        form, so an in-chat quote is not added again.
                                    </>
                                ) : (
                                    <>
                                        Request rows matched back via{' '}
                                        <code>chatbot_session_id</code>. In-chat volume is
                                        appointment and message submissions. Quote volume is{' '}
                                        <code>request_type = quote</code> rows created in the
                                        request form after the chat handed the visitor off.
                                    </>
                                )}
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <KpiTile
                                    label="Total chatbot leads"
                                    value={report.chatbot_impact.total_leads}
                                    color="#0d9488"
                                    sub={`${report.chatbot_impact.in_chat_leads} in-chat + ${report.chatbot_impact.quote_requests} quote`}
                                />
                                <KpiTile
                                    label="Chatbot conversion rate"
                                    value={formatPercent(
                                        report.chatbot_impact.chatbot_conversion_rate_pct,
                                    )}
                                    color="#0d9488"
                                    sub="converting sessions ÷ chat sessions"
                                />
                                <KpiTile
                                    label="In-chat leads"
                                    value={report.chatbot_impact.in_chat_leads}
                                    color="#16a34a"
                                    sub={
                                        quoteFlowStaysInChat(report.filters_applied.source)
                                            ? 'callback / quote request / appointment / message'
                                            : 'appointment / message'
                                    }
                                />
                                <KpiTile
                                    label="Quote requests"
                                    value={report.chatbot_impact.quote_requests}
                                    color="#7c3aed"
                                    sub="request_type quote"
                                />
                            </div>
                        </Card>

                        <Card header="Headline KPIs">
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                                <KpiTile
                                    label="Chats started"
                                    value={report.chats_started.distinct_sessions}
                                    color="#2563eb"
                                    sub={`${report.chats_started.events} open events`}
                                />
                                <KpiTile
                                    label="Wizard opens"
                                    value={report.wizard_opened.distinct_sessions}
                                    color="#7c3aed"
                                    sub={`${report.wizard_opened.events} open events`}
                                />
                                <KpiTile
                                    label="Conversions"
                                    value={report.conversions.total}
                                    color="#16a34a"
                                    sub={`${report.conversions.distinct_sessions} sessions`}
                                />
                                <KpiTile
                                    label="Conversion rate"
                                    value={formatPercent(report.derived.conversion_rate_pct)}
                                    color="#0d9488"
                                    sub="converting sessions ÷ chats"
                                />
                                <KpiTile
                                    label="RAG success rate"
                                    value={formatPercent(report.rag.success_rate_pct)}
                                    color="#0891b2"
                                />
                            </div>
                        </Card>

                        <div className="grid gap-5 lg:grid-cols-2">
                            <Card header="In-chat funnel (distinct sessions)">
                                <p className="text-sm text-grey-500 mb-3">
                                    {quoteFlowStaysInChat(report.filters_applied.source) ? (
                                        <>
                                            Distinct sessions. The quote wizard stays in this
                                            funnel: this bot submits it in the chat and emits{' '}
                                            <code>lead_submitted</code>.
                                        </>
                                    ) : (
                                        <>
                                            Conversions completed inside the chat widget
                                            (appointment / message). Excludes quote sessions because
                                            those hand the user off to the request form and
                                            don&apos;t emit <code>lead_submitted</code> in{' '}
                                            <code>chatbot_events</code>.
                                        </>
                                    )}
                                </p>
                                <div className="grid gap-3 sm:grid-cols-3">
                                    <FunnelStage
                                        label="Chats started"
                                        sessions={report.funnel.chat_sessions}
                                        color="#2563eb"
                                    />
                                    <FunnelStage
                                        label="Opened in-chat wizard"
                                        sessions={report.funnel.in_chat_wizard_sessions}
                                        color="#7c3aed"
                                        rate={report.funnel.in_chat_wizard_open_rate_pct}
                                        rateLabel="of chats"
                                    />
                                    <FunnelStage
                                        label="Submitted in-chat lead"
                                        sessions={report.funnel.in_chat_lead_sessions}
                                        color="#16a34a"
                                        rate={report.funnel.in_chat_lead_conversion_rate_pct}
                                        rateLabel="of chats"
                                        secondaryRate={
                                            report.funnel.in_chat_wizard_to_lead_rate_pct
                                        }
                                        secondaryLabel="of wizard sessions"
                                    />
                                </div>
                            </Card>

                            <Card header="Quote redirect funnel (distinct sessions)">
                                <p className="text-sm text-grey-500 mb-3">
                                    {quoteFlowStaysInChat(report.filters_applied.source) ? (
                                        <>
                                            Sessions that opened the quote wizard and then created a{' '}
                                            <code>request_type = quote</code> row. This bot submits
                                            quotes in chat, so those leads are in the in-chat totals
                                            and this stage stays at 0.
                                        </>
                                    ) : (
                                        <>
                                            Quote requests completed in the request form (
                                            <code>request_type = quote</code>), matched back via{' '}
                                            <code>requests.chatbot_session_id</code>. Counts
                                            distinct sessions, so several requests from one session
                                            stay within the wizard they opened.
                                        </>
                                    )}
                                </p>
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <FunnelStage
                                        label="Quote wizard opened"
                                        sessions={report.quote_redirect.quote_wizard_sessions}
                                        color="#7c3aed"
                                    />
                                    <FunnelStage
                                        label="Quote request created"
                                        sessions={report.quote_redirect.quote_requests}
                                        color="#16a34a"
                                        rate={report.quote_redirect.quote_conversion_rate_pct}
                                        rateLabel="of quote wizard"
                                    />
                                </div>
                            </Card>
                        </div>

                        <Card header="RAG performance">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                <KpiTile
                                    label="RAG success"
                                    value={report.rag.success}
                                    color="#16a34a"
                                />
                                <KpiTile
                                    label="RAG fallback"
                                    value={report.rag.fallback}
                                    color="#dc2626"
                                />
                                <KpiTile
                                    label="Success rate"
                                    value={formatPercent(report.rag.success_rate_pct)}
                                    color="#0891b2"
                                />
                            </div>
                        </Card>

                        <Card header="Wizard by trigger & flow">
                            <p className="text-sm text-grey-500 mb-3">
                                <strong>Trigger</strong> is what made the wizard open:{' '}
                                <code>user_click</code> (CTA tap) or <code>rag_intent</code> (bot
                                inferred intent). <strong>Flow</strong> is which wizard opened.{' '}
                                {quoteFlowStaysInChat(report.filters_applied.source) ? (
                                    <>
                                        <code>quote</code>, <code>appointment</code>, and{' '}
                                        <code>message</code> all stay in chat on this bot.{' '}
                                        <code>quote</code> here is not a redirect.
                                    </>
                                ) : (
                                    <>
                                        <code>quote</code> redirects to the request form;{' '}
                                        <code>appointment</code> and <code>message</code> stay in
                                        chat.
                                    </>
                                )}{' '}
                                The trigger split applies across all flows.{' '}
                                <strong>Attributed</strong> conversions only cover in-chat{' '}
                                <code>lead_submitted</code> events
                                {quoteFlowStaysInChat(report.filters_applied.source)
                                    ? '.'
                                    : ' (quote attribution lives in the Quote redirect funnel above).'}
                            </p>
                            <div className="grid gap-5 lg:grid-cols-2">
                                <BreakdownTable
                                    title="Opens by trigger (sessions)"
                                    rows={Object.entries(report.wizard_opened.by_trigger).map(
                                        ([trigger, bucket]) => ({
                                            label: trigger,
                                            value: `${bucket.distinct_sessions} (${bucket.events} events)`,
                                        }),
                                    )}
                                />
                                <BreakdownTable
                                    title="Opens by flow (sessions)"
                                    rows={Object.entries(report.wizard_opened.by_flow).map(
                                        ([flow, bucket]) => ({
                                            label: flow,
                                            value: `${bucket.distinct_sessions} (${bucket.events} events)`,
                                        }),
                                    )}
                                />
                                <BreakdownTable
                                    title="In-chat attributed conversions"
                                    rows={[
                                        {
                                            label: 'rag_intent (bot)',
                                            value: report.wizard_finished.from_bot,
                                        },
                                        {
                                            label: 'user_click',
                                            value: report.wizard_finished.from_user_click,
                                        },
                                        {
                                            label: 'Total attributed',
                                            value: report.wizard_finished.total_attributed,
                                        },
                                    ]}
                                />
                                <BreakdownTable
                                    title="In-chat completion rate (sessions)"
                                    rows={[
                                        {
                                            label: 'rag_intent',
                                            value: formatPercent(
                                                report.derived.rag_intent_completion_pct,
                                            ),
                                        },
                                        {
                                            label: 'user_click',
                                            value: formatPercent(
                                                report.derived.user_click_completion_pct,
                                            ),
                                        },
                                    ]}
                                />
                            </div>
                        </Card>

                        <Card header="Conversions breakdown">
                            <div className="grid gap-5 lg:grid-cols-2">
                                <KpiTile
                                    label="Total conversions"
                                    value={report.conversions.total}
                                    color="#16a34a"
                                    sub={`${report.conversions.distinct_sessions} distinct sessions`}
                                />
                                <BreakdownTable
                                    title="By type"
                                    rows={Object.entries(report.conversions.by_type).map(
                                        ([type, count]) => ({ label: type, value: count }),
                                    )}
                                />
                            </div>
                        </Card>

                        <Card header="CTA engagement">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                <KpiTile
                                    label="Sessions"
                                    value={report.cta_usage.total_sessions}
                                    color="#2563eb"
                                />
                                <KpiTile
                                    label="With CTA"
                                    value={report.cta_usage.with_cta}
                                    color="#16a34a"
                                    sub={formatShare(
                                        report.cta_usage.with_cta,
                                        report.cta_usage.total_sessions,
                                    )}
                                />
                                <KpiTile
                                    label="Without CTA"
                                    value={report.cta_usage.without_cta}
                                    color="#dc2626"
                                    sub={formatShare(
                                        report.cta_usage.without_cta,
                                        report.cta_usage.total_sessions,
                                    )}
                                />
                            </div>
                        </Card>
                    </div>
                )}
            </div>
        </Page>
    )
}

const withQueryString = (base: string, queryString: string): string =>
    queryString === '' ? base : `${base}?${queryString}`

const formatShare = (count: number, total: number): string =>
    total > 0 ? formatPercent((count * 100) / total) : '–'

interface KpiTileProps {
    label: string
    value: number | string
    color: string
    sub?: string
}

const KpiTile = ({ label, value, color, sub }: KpiTileProps) => (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-center flex flex-col justify-center">
        <div className="text-2xl font-bold tabular-nums" style={{ color }}>
            {value}
        </div>
        <div className="text-sm font-medium text-slate-500 mt-0.5">{label}</div>
        {sub && <div className="text-sm text-slate-400 mt-0.5">{sub}</div>}
    </div>
)

interface FunnelStageProps {
    label: string
    sessions: number
    color: string
    rate?: number
    rateLabel?: string
    secondaryRate?: number
    secondaryLabel?: string
}

const FunnelStage = ({
    label,
    sessions,
    color,
    rate,
    rateLabel,
    secondaryRate,
    secondaryLabel,
}: FunnelStageProps) => (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex flex-col gap-1">
        <div className="text-sm font-medium uppercase tracking-wide text-slate-500">{label}</div>
        <div className="text-3xl font-bold tabular-nums" style={{ color }}>
            {sessions}
        </div>
        <div className="text-sm text-slate-400">sessions</div>
        {rate !== undefined && (
            <div className="mt-1 text-md font-medium text-slate-700">
                {formatPercent(rate)}{' '}
                <span className="text-sm font-normal text-slate-500">{rateLabel}</span>
            </div>
        )}
        {secondaryRate !== undefined && (
            <div className="text-md font-medium text-slate-700">
                {formatPercent(secondaryRate)}{' '}
                <span className="text-sm font-normal text-slate-500">{secondaryLabel}</span>
            </div>
        )}
    </div>
)

interface BreakdownRow {
    label: string
    value: number | string
}

interface BreakdownTableProps {
    title: string
    rows: BreakdownRow[]
}

const BreakdownTable = ({ title, rows }: BreakdownTableProps) => (
    <div className="rounded-lg border border-grey-200 bg-white p-4">
        <h4 className="text-md font-semibold text-grey-700 mb-3">{title}</h4>
        {rows.length === 0 ? (
            <p className="text-md text-grey-500">No data</p>
        ) : (
            <table className="w-full text-md">
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.label} className="border-t border-grey-100 first:border-t-0">
                            <td className="py-1.5 text-grey-700">{row.label}</td>
                            <td className="py-1.5 pl-4 text-right font-bold tabular-nums text-grey-900 whitespace-nowrap">
                                {row.value}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
)

export default ChatbotAnalyticsReportPage
